import { useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { PwaProvider } from "@/context/PwaContext";
import { api } from "@/lib/api";
import { onTippingDomain, MAIN_URL } from "@/lib/domains";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import PayerPage from "@/pages/PayerPage";

import WorkerLayout from "@/worker/WorkerLayout";
import WorkerDashboard from "@/worker/WorkerDashboard";
import WorkerWallet from "@/worker/WorkerWallet";
import WorkerCashout from "@/worker/WorkerCashout";
import WorkerProfile from "@/worker/WorkerProfile";

import AdminLayout from "@/admin/AdminLayout";
import AdminOverview from "@/admin/AdminOverview";
import AdminWorkers from "@/admin/AdminWorkers";
import AdminCards from "@/admin/AdminCards";
import AdminCashouts from "@/admin/AdminCashouts";
import AdminFinance from "@/admin/AdminFinance";
import AdminCorporates from "@/admin/AdminCorporates";
import AdminReports from "@/admin/AdminReports";

function FullLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background" data-testid="app-loader">
      <div className="h-10 w-10 rounded-full border-4 border-primary/20 border-t-primary animate-spin" />
    </div>
  );
}

function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const hash = window.location.hash;
    const sid = new URLSearchParams(hash.replace("#", "")).get("session_id");
    (async () => {
      try {
        const { data } = await api.post("/auth/session", { session_id: sid });
        setUser(data);
        window.history.replaceState(null, "", window.location.pathname);
        navigate(data.role === "worker" ? "/app" : "/admin", { replace: true });
      } catch {
        navigate("/login", { replace: true });
      }
    })();
  }, [navigate, setUser]);
  return <FullLoader />;
}

function homeFor(user) {
  if (!user) return "/login";
  return user.role === "worker" ? "/app" : "/admin";
}

function Protected({ roles, children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to={homeFor(user)} replace />;
  return children;
}

function GuestOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) return <FullLoader />;
  if (user) return <Navigate to={homeFor(user)} replace />;
  return children;
}

function MainRedirect() {
  // On the tipping-only domain, any non-pay route bounces to the main (tiplyme) domain.
  useEffect(() => {
    const target = MAIN_URL ? `${MAIN_URL.replace(/\/$/, "")}${window.location.pathname}${window.location.search}` : "/";
    window.location.replace(target);
  }, []);
  return <FullLoader />;
}

function TippingOnlyRouter() {
  return (
    <Routes>
      <Route path="/pay/:code" element={<PayerPage />} />
      <Route path="/pay/return" element={<PayerPage />} />
      <Route path="/pay/:code/return/:txn" element={<PayerPage />} />
      <Route path="*" element={<MainRedirect />} />
    </Routes>
  );
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  if (onTippingDomain()) return <TippingOnlyRouter />;

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<GuestOnly><Login /></GuestOnly>} />
      <Route path="/register" element={<GuestOnly><Register /></GuestOnly>} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/pay/:code" element={<PayerPage />} />
      <Route path="/pay/return" element={<PayerPage />} />
      <Route path="/pay/:code/return/:txn" element={<PayerPage />} />

      <Route path="/app" element={<Protected roles={["worker"]}><WorkerLayout /></Protected>}>
        <Route index element={<WorkerDashboard />} />
        <Route path="wallet" element={<WorkerWallet />} />
        <Route path="cashout" element={<WorkerCashout />} />
        <Route path="profile" element={<WorkerProfile />} />
      </Route>

      <Route path="/admin" element={<Protected roles={["admin", "sales", "ops"]}><AdminLayout /></Protected>}>
        <Route index element={<AdminOverview />} />
        <Route path="workers" element={<AdminWorkers />} />
        <Route path="cards" element={<AdminCards />} />
        <Route path="cashouts" element={<AdminCashouts />} />
        <Route path="finance" element={<AdminFinance />} />
        <Route path="corporates" element={<AdminCorporates />} />
        <Route path="reports" element={<AdminReports />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <PwaProvider>
        <AuthProvider>
          <BrowserRouter>
            <AppRouter />
            <Toaster position="top-center" richColors />
          </BrowserRouter>
        </AuthProvider>
      </PwaProvider>
    </div>
  );
}
